import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { fetchPhotos, photoUrl } from "@/lib/api";
import { RotatingHero } from "@/components/RotatingHero";

const ABOUT_IMG = "https://images.unsplash.com/photo-1642418425655-7168f530b42d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxtb3RvcmN5Y2xlJTIwY2FmZXxlbnwwfHx8fDE3ODMwMjQ0MDd8MA&ixlib=rb-4.1.0&q=85";

export default function About() {
    const [quoteImages, setQuoteImages] = useState([]);

    useEffect(() => {
        document.title = "About · MotorcycleRoutesIreland";
        fetchPhotos()
            .then((photos) => setQuoteImages(photos.map((p) => photoUrl(p.url))))
            .catch(() => {});
    }, []);

    return (
        <div data-testid="about-page">
            <section className="pt-40 pb-16 border-b border-[var(--border-hair)]">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
                    <div>
                        <div className="eyebrow fade-up">Our Story</div>
                        <h1 className="mt-4 font-display font-black uppercase text-[var(--fg)] text-5xl md:text-7xl lg:text-8xl leading-[0.9] tracking-tighter fade-up fade-up-delay-1">
                            Built by riders.<br />For the road.
                        </h1>
                    </div>
                    <div className="font-body text-lg leading-[1.85] text-[var(--fg-muted)] space-y-5 fade-up fade-up-delay-2">
                        <p className="first-letter:font-display first-letter:text-6xl first-letter:font-black first-letter:text-[#E87722] first-letter:mr-2 first-letter:float-left first-letter:leading-none">
                            MotorcycleRoutesIreland.com started as a shared Google map between three friends who kept ending up on the same twisty roads at weekends. We wanted a better way to remember, share and keep expanding a list of the best roads on this island.
                        </p>
                        <p>
                            Every route on this site has been ridden — often more than once, in every kind of Irish weather — by people who care about the difference between a good road and a great one. The GPX files are the exact tracks we ride. The café stops are the ones we actually pull into.
                        </p>
                        <p>
                            We aren't a tour company. We aren't selling anything. We just want more people to see this country the way it's meant to be seen: at motorcycle pace, from the outside of a bend, with a proper cup of coffee waiting at the far end of the day.
                        </p>
                    </div>
                </div>
            </section>

            <section className="relative min-h-[60vh] overflow-hidden border-b border-[var(--border-hair)]">
                <RotatingHero
                    images={quoteImages}
                    fallback={ABOUT_IMG}
                    className="absolute inset-0"
                    intervalMs={8500}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#14161A] via-[#14161A]/40 to-transparent" />
                <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-40 pb-16 h-full flex items-end on-hero">
                    <blockquote className="max-w-3xl">
                        <div className="font-display font-black uppercase text-[var(--fg)] text-4xl md:text-6xl leading-[1.05] tracking-tighter">
                            "The best roads are the ones you'd ride even if they didn't lead anywhere."
                        </div>
                        <div className="mt-6 text-xs uppercase tracking-[0.3em] text-[#E87722]">— The Guide</div>
                    </blockquote>
                </div>
            </section>

            <section className="py-24">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-3 gap-6">
                    {[
                        { n: "01", h: "Ridden", d: "Every route has been ridden by us. Nothing on this site is a Google-Maps-and-guesswork job." },
                        { n: "02", h: "Free", d: "All routes and GPX files are free. No paywalls, no membership, no upsells." },
                        { n: "03", h: "Respectful", d: "We ride at pace within the law and within the local rhythm. This site advocates for the same." },
                    ].map((v, i) => (
                        <div
                            key={v.n}
                            className="p-8 border border-[var(--border-hair)] bg-[var(--card-bg)] fade-up"
                            style={{ animationDelay: `${i * 80}ms` }}
                        >
                            <div className="font-display font-black text-6xl text-[#E87722] leading-none">{v.n}</div>
                            <div className="mt-6 font-display font-black uppercase text-[var(--fg)] text-2xl tracking-tight">{v.h}</div>
                            <p className="mt-3 font-body text-[var(--fg-muted)] leading-relaxed">{v.d}</p>
                        </div>
                    ))}
                </div>
            </section>

            <section className="py-24 border-t border-[var(--border-hair)] text-center">
                <div className="max-w-3xl mx-auto px-6">
                    <div className="eyebrow">Get In Touch</div>
                    <h2 className="mt-4 font-display font-black uppercase text-[var(--fg)] text-4xl md:text-6xl leading-none tracking-tight">
                        Got a route we've missed?
                    </h2>
                    <p className="mt-6 font-body text-[var(--fg-muted)] text-lg">
                        Tell us. We'll ride it and add it if it earns its place.
                    </p>
                    <Link
                        to="/contact"
                        data-testid="about-contact-cta"
                        className="mt-10 inline-flex items-center gap-3 bg-[#E87722] hover:bg-[#F28C38] text-black px-10 py-4 font-display font-bold uppercase tracking-[0.2em] transition-all"
                    >
                        Send it in <ArrowRight size={18} />
                    </Link>
                </div>
            </section>
        </div>
    );
}
